import axios from 'axios';
import KeycloakService from './KeycloakService';
import { API_BASE_URL, API_TIMEOUT, ENABLE_API_LOGGING } from '../config/api';

/**
 * Service to handle API requests
 */
class ApiService {
  /**
   * Create axios instance with authentication headers
   * @returns {Object} Axios instance
   */
  getAxiosInstance = () => {
    const axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add authentication token to requests if user is authenticated
    axiosInstance.interceptors.request.use((config) => {
      const token = KeycloakService.getToken();
      if (token) {
        if (ENABLE_API_LOGGING) {
          console.log('Adding auth token to request:', config.url);
        }
        config.headers.Authorization = `Bearer ${token}`;
      } else if (ENABLE_API_LOGGING) {
        console.warn('No token available for request:', config.url);
      }
      return config;
    });

    // Handle 401 Unauthorized errors
    axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (ENABLE_API_LOGGING) {
          console.error('API error response:', error.response?.status, error.config?.url);
        }
        if (error.response && error.response.status === 401) {
          if (ENABLE_API_LOGGING) {
            console.log('Received 401, attempting to refresh token');
          }
          // Token expired or invalid, try to refresh
          return KeycloakService.getKeycloak().updateToken(5)
            .then(() => {
              // Retry the request with the new token
              if (ENABLE_API_LOGGING) {
                console.log('Token refreshed, retrying request');
              }
              const originalRequest = error.config;
              originalRequest.headers.Authorization = `Bearer ${KeycloakService.getToken()}`;
              return axios(originalRequest);
            })
            .catch((refreshError) => {
              if (ENABLE_API_LOGGING) {
                console.error('Failed to refresh token:', refreshError);
              }
              // Unable to refresh token, logout
              KeycloakService.logout();
              return Promise.reject(error);
            });
        }
        return Promise.reject(error);
      }
    );

    return axiosInstance;
  };

  /**
   * Get user information
   * @returns {Promise} Promise that resolves with user information
   */
  getUserInfo = async () => {
    try {
      const response = await this.getAxiosInstance().get('/auth/me');
      return response.data;
    } catch (error) {
      console.error('Error getting user info:', error);
      throw error;
    }
  };

  /**
   * Get all products
   * @returns {Promise} Promise that resolves with products
   */
  getProducts = async () => {
    try {
      const response = await this.getAxiosInstance().get('/test/products');
      return response.data;
    } catch (error) {
      console.error('Error getting products:', error);
      throw error;
    }
  };

  /**
   * Get product by ID
   * @param {Number} id Product ID
   * @returns {Promise} Promise that resolves with product
   */
  getProductById = async (id) => {
    try {
      const response = await this.getAxiosInstance().get(`/test/products/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting product ${id}:`, error);
      throw error;
    }
  };

  /**
   * Create a new product
   * @param {Object} product Product data
   * @returns {Promise} Promise that resolves with created product
   */
  createProduct = async (product) => {
    try {
      const response = await this.getAxiosInstance().post('/test/products', product);
      return response.data;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  };

  /**
   * Update a product
   * @param {Number} id Product ID
   * @param {Object} product Product data
   * @returns {Promise} Promise that resolves with updated product
   */
  updateProduct = async (id, product) => {
    try {
      const response = await this.getAxiosInstance().put(`/test/products/${id}`, product);
      return response.data;
    } catch (error) {
      console.error(`Error updating product ${id}:`, error);
      throw error;
    }
  };

  /**
   * Delete a product
   * @param {Number} id Product ID
   * @returns {Promise} Promise that resolves with deleted product
   */
  deleteProduct = async (id) => {
    try {
      const response = await this.getAxiosInstance().delete(`/test/products/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting product ${id}:`, error);
      throw error;
    }
  };

  /**
   * Get sales reports
   * @returns {Promise} Promise that resolves with sales reports
   */
  getSalesReports = async () => {
    try {
      const response = await this.getAxiosInstance().get('/test/products/reports/sales');
      return response.data;
    } catch (error) {
      console.error('Error getting sales reports:', error);
      throw error;
    }
  };

  /**
   * Get all users (admin only)
   * @returns {Promise} Promise that resolves with users
   */
  getUsers = async () => {
    try {
      const response = await this.getAxiosInstance().get('/users');
      return response.data;
    } catch (error) {
      console.error('Error getting users:', error);
      throw error;
    }
  };

  /**
   * Get user roles (admin only)
   * @param {String} userId User ID
   * @returns {Promise} Promise that resolves with user roles
   */
  getUserRoles = async (userId) => {
    try {
      const response = await this.getAxiosInstance().get(`/users/${userId}/roles`);
      return response.data;
    } catch (error) {
      console.error(`Error getting roles for user ${userId}:`, error);
      throw error;
    }
  };

  /**
   * Get available roles for user (admin only)
   * @param {String} userId User ID
   * @returns {Promise} Promise that resolves with available roles
   */
  getAvailableRoles = async (userId) => {
    try {
      const response = await this.getAxiosInstance().get(`/users/${userId}/available-roles`);
      return response.data;
    } catch (error) {
      console.error(`Error getting available roles for user ${userId}:`, error);
      throw error;
    }
  };

  /**
   * Assign roles to user (admin only)
   * @param {String} userId User ID
   * @param {Array} roles Roles to assign
   * @returns {Promise} Promise that resolves when roles are assigned
   */
  assignRoles = async (userId, roles) => {
    try {
      const response = await this.getAxiosInstance().post(`/users/${userId}/roles`, { roles });
      return response.data;
    } catch (error) {
      console.error(`Error assigning roles to user ${userId}:`, error);
      throw error;
    }
  };

  /**
   * Remove roles from user (admin only)
   * @param {String} userId User ID
   * @param {Array} roles Roles to remove
   * @returns {Promise} Promise that resolves when roles are removed
   */
  removeRoles = async (userId, roles) => {
    try {
      const response = await this.getAxiosInstance().delete(`/users/${userId}/roles`, { data: { roles } });
      return response.data;
    } catch (error) {
      console.error(`Error removing roles from user ${userId}:`, error);
      throw error;
    }
  };
}

export default new ApiService();